import { useState, type FormEvent } from "react";
import type { AdminStore } from "@workspace/api-client-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Loader2 } from "lucide-react";
import { formatCurrency } from "@/lib/currency";

const BASE = import.meta.env.BASE_URL.replace(/\/$/, "");
function watDay(value: Date | string) {
  const parts = new Intl.DateTimeFormat("en", {
    timeZone: "Africa/Lagos", year: "numeric", month: "2-digit", day: "2-digit",
  }).formatToParts(new Date(value));
  const part = (type: string) => parts.find(item => item.type === type)?.value ?? "";
  return `${part("year")}-${part("month")}-${part("day")}`;
}

export function AdminTransferDateCorrection({ store, onSaved }: { store: AdminStore; onSaved: () => Promise<void> }) {
  const [open, setOpen] = useState(false);
  const [startsAt, setStartsAt] = useState("");
  const [endsAt, setEndsAt] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const subscription = store.latestSubscription;
  if (store.isDemo || subscription?.paymentMethod !== "manual_transfer" || !subscription.endsAt) return null;

  function openCorrection() {
    setStartsAt(watDay(subscription!.startsAt));
    setEndsAt(watDay(subscription!.endsAt!));
    setError("");
    setOpen(true);
  }

  async function save(event: FormEvent) {
    event.preventDefault();
    if (!subscription?.endsAt) return;
    setError("");
    if (endsAt <= startsAt) {
      setError("The end date must be after the start date.");
      return;
    }
    setSaving(true);
    try {
      const response = await fetch(`${BASE}/api/admin/stores/${store.id}/manual-subscription`, {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          subscriptionId: subscription.id,
          expectedStartsAt: subscription.startsAt,
          expectedEndsAt: subscription.endsAt,
          startsAt,
          endsAt,
        }),
      });
      const data = await response.json();
      if (!response.ok) {
        setError(data.error || "Could not correct the dates.");
        return;
      }
      await onSaved();
      setOpen(false);
    } catch {
      setError("Could not confirm the correction. Reload the business list to check the saved dates before trying again.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <>
      <Button variant="outline" size="sm" className="h-7 px-2 text-xs" onClick={openCorrection}>Correct transfer dates</Button>
      <Dialog open={open} onOpenChange={value => { if (!saving) setOpen(value); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Correct bank-transfer dates</DialogTitle>
            <DialogDescription>
              {store.name} — this edits the existing transfer, not a new payment.
              The {formatCurrency(subscription.amount)} received, plan, and billing history count stay unchanged.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <label className="block space-y-1 text-sm">
              <span>Correct start date</span>
              <Input type="date" required max={watDay(new Date())} value={startsAt} onChange={event => setStartsAt(event.target.value)} disabled={saving} />
            </label>
            <label className="block space-y-1 text-sm">
              <span>Correct end date</span>
              <Input type="date" required min={startsAt} value={endsAt} onChange={event => setEndsAt(event.target.value)} disabled={saving} />
            </label>
            <p className="text-sm text-muted-foreground">Saving replaces the existing paid period and updates account access through 6:00 PM WAT on the end date. No payment or Paystack renewal is created.</p>
            {error && <p role="alert" className="text-sm text-destructive">{error}</p>}
            <DialogFooter>
              <Button type="button" variant="outline" disabled={saving} onClick={() => setOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={saving}>
                {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Save corrected dates
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
